import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';
import { useOrganization } from '../hooks/useOrganization'; 
import { BarChart2, Calendar, Users, UserCheck, UserX, Filter, X, Search, Download, Building, RefreshCw, Loader2 } from 'lucide-react';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';
import { Bar } from 'react-chartjs-2';
import LiquidGlass from './LiquidGlass';

// Register Chart.js components
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

interface DashboardStats {
  totalEvents: number;
  totalParticipants: number;
  finishers: number;
  dnfRate: number;
  genderDistribution: {
    Male: number;
    Female: number;
    'Non Binary': number;
    Other: number;
  };
}

interface Filter {
  type: 'all' | 'date' | 'event';
  dateRange?: 'week' | 'month' | 'year' | 'custom' | 'all';
  startDate?: string;
  endDate?: string;
  eventId?: string;
}

interface Event {
  id: string;
  name: string;
  date: string;
}

interface TimeDistribution {
  timeWindow: string;
  count: number;
  startMinutes: number;
}

export default function Dashboard() {
  const { user } = useAuth();
  const { organization, loading: orgLoading } = useOrganization();
  const [loadingOrganizationData, setLoadingOrganizationData] = useState(true);
  const [loadingResultsStatus, setLoadingResultsStatus] = useState(true);
  const [stats, setStats] = useState<DashboardStats>({
    totalEvents: 0,
    totalParticipants: 0,
    finishers: 0,
    dnfRate: 0,
    genderDistribution: {
      Male: 0,
      Female: 0,
      'Non Binary': 0,
      Other: 0
    }
  });
  const [timeDistribution, setTimeDistribution] = useState<TimeDistribution[]>([]);
  const [loadingStats, setLoadingStats] = useState(true);
  const [loadingChart, setLoadingChart] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>({ type: 'all' });
  const [events, setEvents] = useState<Event[]>([]);
  const [showFilters, setShowFilters] = useState(false);
  const [eventSearchTerm, setEventSearchTerm] = useState('');
  const [showEventDropdown, setShowEventDropdown] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [organizationEvents, setOrganizationEvents] = useState<Event[]>([]);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false); 

  useEffect(() => {
    if (user) {
      loadEvents();
      if (organization) {
        loadOrganizationEvents();
      }
    }
  }, [user, organization]);

  useEffect(() => {
    // Handle clicks outside of the dropdown
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowEventDropdown(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadEvents = async () => {
    try {
      const { data, error } = await supabase
        .from('events')
        .select('id, name, date')
        .eq('user_id', user?.id)
        .order('date', { ascending: false });

      if (error) throw error;
      setEvents(data || []);
    } catch (err: any) {
      console.error('Error loading events:', err);
    }
  };

  const loadOrganizationEvents = async () => {
    if (!organization?.id) return;
    
    try {
      const { data, error } = await supabase
        .from('events')
        .select('id, name, date')
        .eq('organization_id', organization.id)
        .order('date', { ascending: false });

      if (error) throw error;
      
      // Combine with personal events, avoiding duplicates
      setOrganizationEvents(data || []);
    } catch (err: any) {
      console.error('Error loading organization events:', err);
    }
  };

  const getDateRange = () => {
    const today = new Date();
    switch (filter.dateRange) {
      case 'week':
        const lastWeek = new Date(today);
        lastWeek.setDate(lastWeek.getDate() - 7);
        return lastWeek;
      case 'month':
        const lastMonth = new Date(today);
        lastMonth.setMonth(lastMonth.getMonth() - 1);
        return lastMonth;
      case 'year':
        const lastYear = new Date(today);
        lastYear.setFullYear(lastYear.getFullYear() - 1);
        return lastYear;
      case 'custom':
        return filter.startDate ? new Date(filter.startDate) : null;
      default:
        return null;
    }
  };

  // Advanced outlier detection using IQR method with timing chip error handling
  const removeOutliers = (times: number[]) => {
    if (times.length < 4) return times; // Need at least 4 data points for IQR

    const sorted = [...times].sort((a, b) => a - b);
    const q1Index = Math.floor(sorted.length * 0.25);
    const q3Index = Math.floor(sorted.length * 0.75);
    const q1 = sorted[q1Index];
    const q3 = sorted[q3Index];
    const iqr = q3 - q1;
    
    // Use a more aggressive multiplier for timing chip errors (1.5 instead of 3)
    // This will catch more extreme outliers that are likely timing errors
    const lowerBound = q1 - 1.5 * iqr;
    const upperBound = q3 + 1.5 * iqr;
    
    const filtered = times.filter(time => time >= lowerBound && time <= upperBound);
    
    // Log outlier removal for debugging
    const outliers = times.filter(time => time < lowerBound || time > upperBound);
    if (outliers.length > 0) {
      console.log(`Removed ${outliers.length} outliers:`, outliers.map(t => `${Math.floor(t/60)}:${Math.floor(t%60).toString().padStart(2, '0')}`));
    }
    
    return filtered;
  };

  // Improved bracket calculation with strict bar count enforcement
  const calculateOptimalBrackets = (times: number[], eventDistance?: number) => {
    if (times.length === 0) return { targetBrackets: 10, bracketSize: 5 };

    const minTime = Math.min(...times);
    const maxTime = Math.max(...times);
    const timeRange = maxTime - minTime;

    // Enforce minimum and maximum bar counts
    const MIN_BARS = 8;
    const MAX_BARS = 20;
    
    // Calculate initial bracket size based on time range and desired bar count
    let targetBrackets: number;
    
    if (eventDistance) {
      // Distance-based initial calculation
      if (eventDistance <= 5) {
        // Short events (5K and under) - aim for 12-16 bars
        targetBrackets = Math.min(16, Math.max(12, Math.ceil(timeRange / 2)));
      } else if (eventDistance <= 21) {
        // Medium events (10K to half marathon) - aim for 10-15 bars
        targetBrackets = Math.min(15, Math.max(10, Math.ceil(timeRange / 5)));
      } else {
        // Long events (marathon and ultra) - aim for 8-12 bars
        targetBrackets = Math.min(12, Math.max(8, Math.ceil(timeRange / 10)));
      }
    } else {
      // Default calculation when distance is unknown - use time range
      if (timeRange <= 30) {
        // Very tight field (30 min spread) - more granular
        targetBrackets = Math.min(15, Math.max(12, Math.ceil(timeRange / 2)));
      } else if (timeRange <= 120) {
        // Moderate spread (2 hour spread) - balanced
        targetBrackets = Math.min(16, Math.max(10, Math.ceil(timeRange / 8)));
      } else if (timeRange <= 300) {
        // Wide spread (5 hour spread) - fewer bars
        targetBrackets = Math.min(14, Math.max(8, Math.ceil(timeRange / 20)));
      } else {
        // Very wide spread (ultra events) - minimal bars
        targetBrackets = Math.min(12, Math.max(8, Math.ceil(timeRange / 30)));
      }
    }

    // Enforce strict limits
    targetBrackets = Math.max(MIN_BARS, Math.min(MAX_BARS, targetBrackets));
    
    // Calculate bracket size to achieve target number of bars
    let bracketSize = timeRange / targetBrackets;
    
    // Round bracket size to nice numbers for better readability
    if (bracketSize <= 1) {
      bracketSize = Math.max(0.5, Math.round(bracketSize * 4) / 4); // Round to nearest 0.25
    } else if (bracketSize <= 2) {
      bracketSize = Math.max(0.5, Math.round(bracketSize * 2) / 2); // Round to nearest 0.5
    } else if (bracketSize <= 5) {
      bracketSize = Math.max(1, Math.round(bracketSize)); // Round to nearest 1
    } else if (bracketSize <= 15) {
      bracketSize = Math.max(2, Math.ceil(bracketSize / 2) * 2); // Round to nearest 2
    } else if (bracketSize <= 30) {
      bracketSize = Math.max(5, Math.ceil(bracketSize / 5) * 5); // Round to nearest 5
    } else {
      bracketSize = Math.max(10, Math.ceil(bracketSize / 10) * 10); // Round to nearest 10
    }

    // Verify we'll get the right number of bars and adjust if needed
    let actualBars = Math.ceil(timeRange / bracketSize);
    
    // If we're still outside the range, force adjust
    if (actualBars < MIN_BARS) {
      bracketSize = timeRange / MIN_BARS;
      // Re-round to nice numbers
      if (bracketSize <= 1) {
        bracketSize = Math.max(0.25, Math.round(bracketSize * 4) / 4);
      } else if (bracketSize <= 5) {
        bracketSize = Math.max(0.5, Math.round(bracketSize * 2) / 2);
      } else {
        bracketSize = Math.max(1, Math.round(bracketSize));
      }
      actualBars = Math.ceil(timeRange / bracketSize);
    } else if (actualBars > MAX_BARS) {
      bracketSize = timeRange / MAX_BARS;
      // Re-round to nice numbers
      if (bracketSize <= 5) {
        bracketSize = Math.ceil(bracketSize);
      } else {
        bracketSize = Math.ceil(bracketSize / 5) * 5;
      }
      actualBars = Math.ceil(timeRange / bracketSize);
    }

    // Final verification and emergency adjustment
    if (actualBars < MIN_BARS || actualBars > MAX_BARS) {
      // Emergency fallback - use simple division
      bracketSize = timeRange / Math.max(MIN_BARS, Math.min(MAX_BARS, Math.round(timeRange / 5)));
      actualBars = Math.ceil(timeRange / bracketSize);
    }

    return { targetBrackets: actualBars, bracketSize };
  };

  const formatTimeWindow = (startMinutes: number, bracketSize: number) => {
    const endMinutes = startMinutes + bracketSize;
    
    const formatTime = (minutes: number) => {
      const hours = Math.floor(minutes / 60);
      const mins = Math.floor(minutes % 60);
      const secs = Math.round((minutes % 1) * 60);
      
      if (hours > 0) {
        return `${hours}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
      } else {
        return `${mins}:${secs.toString().padStart(2, '0')}`;
      }
    };
    
    return `${formatTime(startMinutes)} - ${formatTime(endMinutes)}`;
  };

  const loadTimeDistribution = async (eventId: string) => {
    setLoadingChart(true);
    try {
      // Get event details for distance-based optimization
      const { data: eventData } = await supabase
        .from('events')
        .select('distance')
        .eq('id', eventId)
        .single();

      // First get the total count of valid results
      const { count, error: countError } = await supabase
        .from('results')
        .select('*', { count: 'exact', head: true })
        .eq('event_id', eventId)
        .not('chip_time', 'eq', 'DNF')
        .not('chip_time', 'is', null);

      if (countError) throw countError;
      if (!count) {
        setTimeDistribution([]);
        return;
      }

      // Fetch all results in batches
      const batchSize = 1000;
      const batches = Math.ceil(count / batchSize);
      let allTimes: number[] = [];

      for (let i = 0; i < batches; i++) {
        const start = i * batchSize;
        const { data, error } = await supabase
          .from('results')
          .select('chip_time')
          .eq('event_id', eventId)
          .not('chip_time', 'eq', 'DNF')
          .not('chip_time', 'is', null)
          .range(start, start + batchSize - 1);

        if (error) throw error;
        if (!data) continue;

        // Convert times to minutes and add to array
        const batchTimes = data
          .map(r => {
            const [hours, minutes, seconds] = r.chip_time.split(':').map(Number);
            return hours * 60 + minutes + seconds / 60;
          })
          .filter(time => !isNaN(time));

        allTimes = allTimes.concat(batchTimes);
      }

      // Sort all times
      allTimes.sort((a, b) => a - b);

      if (allTimes.length === 0) {
        setTimeDistribution([]);
        return;
      }

      // Remove outliers to handle timing chip errors
      const cleanedTimes = removeOutliers(allTimes);
      
      if (cleanedTimes.length === 0) {
        setTimeDistribution([]);
        return;
      }

      // Calculate optimal brackets with strict bar count enforcement
      const { bracketSize, targetBrackets } = calculateOptimalBrackets(cleanedTimes, eventData?.distance);

      // Create distribution with optimized time windows
      const distribution: { [key: number]: number } = {};
      const minTime = Math.min(...cleanedTimes);
      
      // Start from the minimum time rounded down to the nearest bracket
      const startBucket = Math.floor(minTime / bracketSize) * bracketSize;
      
      cleanedTimes.forEach(time => {
        const bucketStart = Math.floor((time - startBucket) / bracketSize) * bracketSize + startBucket;
        distribution[bucketStart] = (distribution[bucketStart] || 0) + 1;
      });

      // Convert to array format for Chart.js and ensure we have the right number of bars
      const timeDistData = Object.entries(distribution)
        .map(([startMinutes, count]) => ({
          timeWindow: formatTimeWindow(parseFloat(startMinutes), bracketSize),
          count,
          startMinutes: parseFloat(startMinutes)
        }))
        .sort((a, b) => a.startMinutes - b.startMinutes);

      // Log results for debugging
      const outlierCount = allTimes.length - cleanedTimes.length;
      console.log(`Generated ${timeDistData.length} bars for ${cleanedTimes.length} participants (${outlierCount} outliers removed)`);
      console.log(`Time range: ${(Math.max(...cleanedTimes) - Math.min(...cleanedTimes)).toFixed(1)} minutes with bracket size ${bracketSize.toFixed(1)}`);

      setTimeDistribution(timeDistData);
    } catch (err: any) {
      console.error('Error loading time distribution:', err);
      setError('Failed to load finish time distribution');
    } finally {
      setLoadingChart(false);
    }
  };

  const loadStats = useCallback(async () => {
    if (!user?.id) return;
    
    setLoadingStats(true);
    setLoadingResultsStatus(true);
    setError(null);
    
    try {
      let params: any = {
        p_user_id: user.id
      };
      
      // If user belongs to an organization, we want to show organization-wide stats
      if (organization?.id) {
        params.p_organization_id = organization.id;
        setLoadingOrganizationData(true);
      }

      // Add date filters if applicable
      if (filter.type === 'date' && filter.dateRange !== 'all') {
        const startDate = getDateRange();
        if (startDate) {
          params.p_start_date = startDate.toISOString().split('T')[0];
        }
        if (filter.dateRange === 'custom' && filter.endDate) {
          params.p_end_date = filter.endDate;
        }
      }

      // Add event filter if applicable
      if (filter.type === 'event' && filter.eventId) {
        params.p_event_ids = [filter.eventId];
      }

      const { data: statsData, error: statsError } = await supabase
        .rpc('get_dashboard_stats', params);

      if (statsError) throw statsError;

      if (statsData && statsData[0]) {
        const {
          total_events,
          total_participants,
          finishers,
          dnf_rate,
          gender_stats
        } = statsData[0];

        // Calculate "Other" by subtracting known genders from total
        const knownGenderCount = (gender_stats.Male || 0) + 
                               (gender_stats.Female || 0) + 
                               (gender_stats['Non Binary'] || 0);
        const otherCount = total_participants - knownGenderCount;

        setStats({
          totalEvents: total_events,
          totalParticipants: total_participants,
          finishers,
          dnfRate: dnf_rate,
          genderDistribution: {
            ...gender_stats,
            Other: otherCount
          }
        });

        // Load time distribution data if a specific event is selected
        if (filter.type === 'event' && filter.eventId) {
          await loadTimeDistribution(filter.eventId);
        } else {
          setTimeDistribution([]);
        }
      }
    } catch (err: any) {
      console.error('Error loading stats:', err);
      setError(err.message || 'Failed to load dashboard statistics');
    } finally {
      // Use setTimeout to ensure the loading state is visible for at least a moment
      // This prevents flickering between loading states
      setTimeout(() => {
        setLoadingStats(false);
        setLoadingOrganizationData(false);
        setLoadingResultsStatus(false);
        setInitialLoadComplete(true);
        setIsRefreshing(false);
      }, 300);
    }
  }, [user?.id, filter, organization?.id, orgLoading]);

  useEffect(() => {
    // Only load stats when we have user info and organization status is determined
    if (user && !orgLoading) { 
      loadStats();
    }
  }, [user, orgLoading, loadStats]);

  const handleRefresh = () => {
    setIsRefreshing(true);
    setLoadingOrganizationData(true);
    setLoadingResultsStatus(true);
    loadStats();
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const clearFilters = () => {
    setFilter({ type: 'all' });
    setShowFilters(false);
    setSelectedEvent(null);
    setEventSearchTerm('');
  };

  // Combine personal and organization events for filtering
  const allEvents = useMemo(() => {
    // Create a Map to deduplicate events by ID
    const eventsMap = new Map();
    
    // Add personal events
    events.forEach(event => {
      eventsMap.set(event.id, event);
    });
    
    // Add organization events
    organizationEvents.forEach(event => {
      if (!eventsMap.has(event.id)) {
        eventsMap.set(event.id, event);
      }
    });
    
    // Convert Map back to array
    return Array.from(eventsMap.values());
  }, [events, organizationEvents]);

  // Filter events based on search term
  const filteredEvents = useMemo(() => {
    if (!eventSearchTerm.trim()) {
      return allEvents;
    }
    
    const term = eventSearchTerm.toLowerCase().trim();
    return allEvents.filter(event => 
      event.name.toLowerCase().includes(term) || 
      formatDate(event.date).includes(term)
    );
  }, [eventSearchTerm, allEvents]);

  const handleEventSelect = (event: Event) => {
    setSelectedEvent(event);
    setEventSearchTerm(event.name);
    setFilter({
      type: 'event',
      eventId: event.id
    });
    setShowEventDropdown(false);
  };

  // Chart.js configuration with black glass bars
  const chartData = {
    labels: timeDistribution.map(d => d.timeWindow),
    datasets: [
      {
        label: 'Number of Finishers',
        data: timeDistribution.map(d => d.count),
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        borderColor: 'rgba(0, 0, 0, 0.9)',
        borderWidth: 1,
        borderRadius: 4,
        borderSkipped: false,
      }
    ]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.9)',
        titleColor: 'white',
        bodyColor: 'white',
        borderColor: 'rgba(255, 255, 255, 0.2)',
        borderWidth: 1,
        cornerRadius: 8,
        callbacks: {
          title: (context: any) => `Time: ${context[0].label}`,
          label: (context: any) => `${context.parsed.y} finisher${context.parsed.y !== 1 ? 's' : ''}`
        }
      }
    },
    scales: {
      x: {
        title: {
          display: true,
          text: 'Finish Time',
          color: '#374151',
          font: {
            size: 12,
            weight: 'bold'
          }
        },
        ticks: {
          color: '#6B7280',
          font: {
            size: 11
          },
          maxRotation: 45,
          minRotation: 0
        },
        grid: {
          color: 'rgba(0, 0, 0, 0.1)',
          lineWidth: 1
        }
      },
      y: {
        title: {
          display: true,
          text: 'Number of Finishers',
          color: '#374151',
          font: {
            size: 12,
            weight: 'bold'
          }
        },
        ticks: {
          color: '#6B7280',
          font: {
            size: 11
          },
          precision: 0
        },
        grid: {
          color: 'rgba(0, 0, 0, 0.1)',
          lineWidth: 1
        },
        beginAtZero: true
      }
    },
    interaction: {
      intersect: false,
      mode: 'index' as const
    }
  };

  // Show loading state when we're loading organization data or initial stats
  if (loadingStats || loadingOrganizationData || loadingResultsStatus || orgLoading) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold text-gray-800">Dashboard</h1>
          <div className="flex items-center gap-3">
            {organization ? (
              <div className="flex items-center gap-2 text-gray-600 bg-gray-100/80 px-3 py-1.5 rounded-full">
                <Building className="w-4 h-4" />
                <span className="text-sm font-medium">{organization.name}</span>
              </div>
            ) : (
              <div className="h-8 w-40 bg-gray-200 animate-pulse rounded-full"></div>
            )}
            <Loader2 className="w-5 h-5 text-gray-400 animate-spin" />
          </div>
        </div>

        {/* Stats Cards Skeleton */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[...Array(4)].map((_, index) => (
            <div key={index} className="bg-white p-6 rounded-lg shadow">
              <div className="flex items-center gap-4">
                <div className="h-12 w-12 bg-gray-200 animate-pulse rounded-xl"></div>
                <div>
                  <div className="h-5 w-24 bg-gray-200 animate-pulse rounded mb-2"></div>
                  <div className="h-8 w-16 bg-gray-200 animate-pulse rounded"></div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Gender Distribution Skeleton */}
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="h-6 w-48 bg-gray-200 animate-pulse rounded mb-6"></div>
          <div className="space-y-6">
            {[...Array(4)].map((_, index) => (
              <div key={index}>
                <div className="flex justify-between mb-2">
                  <div className="h-5 w-20 bg-gray-200 animate-pulse rounded"></div>
                  <div className="h-5 w-10 bg-gray-200 animate-pulse rounded"></div>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-gray-300 h-2 rounded-full"
                    style={{ width: `${Math.random() * 100}%` }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Time Distribution Skeleton */}
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="h-6 w-56 bg-gray-200 animate-pulse rounded mb-2"></div>
          <div className="h-5 w-full bg-gray-200 animate-pulse rounded mb-6"></div>
          <div className="h-[400px] bg-gray-100 rounded-lg animate-pulse"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-3">
          <h1 className="text-3xl font-bold text-gray-800">Dashboard</h1> 
          {organization && (
            <div className="flex items-center gap-2 text-gray-600 bg-gray-100/80 px-3 py-1.5 rounded-full">
              <Building className="w-4 h-4" />
              <span className="text-sm font-medium">{organization.name}</span>
            </div>
          )}
        </div>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full sm:w-auto">
          <LiquidGlass variant="button" hover className="order-1 sm:order-0">
            <button
              onClick={handleRefresh} 
              disabled={loadingStats || loadingOrganizationData || loadingResultsStatus || isRefreshing}
              className="flex items-center justify-center gap-2 px-3 py-2 text-gray-600 hover:text-gray-800 transition-colors duration-200"
              title="Refresh dashboard"
            >
              <RefreshCw className={`w-4 h-4 ${isRefreshing || loadingStats || loadingOrganizationData || loadingResultsStatus ? 'animate-spin' : ''}`} />
              <span className="hidden sm:inline">Refresh</span>
            </button>
          </LiquidGlass>
          <LiquidGlass variant="button" hover className="order-2 sm:order-1">
            <button
              onClick={async () => {
                try {
                  // Prepare the data
                  const data = [
                    // Header row
                    ['Report Type', filter.type === 'all' ? 'All Time' : filter.type === 'date' ? 'Date Range' : 'Single Event'],
                    ['Date Generated', new Date().toLocaleString()],
                    filter.type === 'event' && selectedEvent ? 
                      ['Event', `${selectedEvent.name} (${formatDate(selectedEvent.date)})`] : 
                      null,
                    filter.type === 'date' ? 
                      ['Date Range', `${filter.startDate || 'All'} to ${filter.endDate || 'Present'}`] : 
                      null,
                    [''], // Empty row for spacing
                    ['Overview'],
                    ['Total Events', stats.totalEvents],
                    ['Total Participants', stats.totalParticipants],
                    ['Finishers', stats.finishers],
                    ['DNF Rate', `${stats.dnfRate}%`],
                    [''], // Empty row for spacing
                    ['Gender Distribution'],
                    ['Gender', 'Count', 'Percentage'],
                    ...Object.entries(stats.genderDistribution).map(([gender, count]) => [
                      gender,
                      count,
                      `${((count / stats.totalParticipants) * 100).toFixed(1)}%`
                    ])
                  ].filter(Boolean); // Remove null rows

                  // Convert to CSV
                  const csvContent = data
                    .map(row => row.map(cell => 
                      typeof cell === 'string' && cell.includes(',') ? 
                        `"${cell}"` : 
                        cell
                    ).join(','))
                    .join('\n');

                  // Create and trigger download
                  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
                  const link = document.createElement('a');
                  const filename = `dashboard_report_${new Date().toISOString().split('T')[0]}.csv`;
                  
                  if (navigator.msSaveBlob) { // IE10+
                    navigator.msSaveBlob(blob, filename);
                  } else {
                    link.href = URL.createObjectURL(blob);
                    link.setAttribute('download', filename);
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                  }
                } catch (err) {
                  console.error('Error exporting data:', err);
                  setError('Failed to export data');
                }
              }}
              className="flex items-center justify-center gap-2 px-3 sm:px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors duration-200 w-full sm:w-auto"
            >
              <Download className="w-4 h-4" />
              <span className="sm:inline">Export Report</span>
            </button>
          </LiquidGlass>
          
          {filter.type !== 'all' && (
            <LiquidGlass variant="button" hover className="order-3 sm:order-2">
              <button
                onClick={clearFilters}
                className="flex items-center justify-center gap-2 px-3 sm:px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors duration-200 w-full sm:w-auto"
              >
                <X className="w-4 h-4" />
                <span className="sm:inline">Clear Filters</span>
              </button>
            </LiquidGlass>
          )}
          
          <LiquidGlass variant="button" hover className="order-1 sm:order-3">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center justify-center gap-2 px-3 sm:px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors duration-200 w-full sm:w-auto"
            >
              <Filter className="w-4 h-4" />
              <span className="sm:inline">Filters</span>
            </button>
          </LiquidGlass>
        </div>
      </div>

      {showFilters && (
        <LiquidGlass variant="card" className="p-6 space-y-4">
          <h2 className="font-semibold text-gray-800">Filter Data</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Filter Type
              </label>
              <select
                value={filter.type}
                onChange={(e) => {
                  setFilter({ type: e.target.value as Filter['type'] });
                  if (e.target.value !== 'event') {
                    setSelectedEvent(null);
                    setEventSearchTerm('');
                  }
                }}
                className="w-full px-3 py-2 bg-white/60 border border-gray-200 rounded-lg text-gray-800 focus:border-blue-300 focus:ring-2 focus:ring-blue-200"
              >
                <option value="all">All Data</option>
                <option value="date">Date Range</option>
                <option value="event">Specific Event</option>
              </select>
            </div>

            {filter.type === 'date' && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Date Range
                  </label>
                  <select
                    value={filter.dateRange || 'all'}
                    onChange={(e) => setFilter({ 
                      ...filter, 
                      dateRange: e.target.value as Filter['dateRange']
                    })}
                    className="w-full px-3 py-2 bg-white/60 border border-gray-200 rounded-lg text-gray-800 focus:border-blue-300 focus:ring-2 focus:ring-blue-200"
                  >
                    <option value="all">All Time</option>
                    <option value="week">Last 7 Days</option>
                    <option value="month">Last 30 Days</option>
                    <option value="year">Last Year</option>
                    <option value="custom">Custom Range</option>
                  </select>
                </div>

                {filter.dateRange === 'custom' && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Start Date
                      </label>
                      <input
                        type="date"
                        value={filter.startDate || ''}
                        onChange={(e) => setFilter({
                          ...filter,
                          startDate: e.target.value
                        })}
                        className="w-full px-3 py-2 bg-white/60 border border-gray-200 rounded-lg text-gray-800 focus:border-blue-300 focus:ring-2 focus:ring-blue-200"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        End Date
                      </label>
                      <input
                        type="date"
                        value={filter.endDate || ''}
                        onChange={(e) => setFilter({
                          ...filter,
                          endDate: e.target.value
                        })}
                        className="w-full px-3 py-2 bg-white/60 border border-gray-200 rounded-lg text-gray-800 focus:border-blue-300 focus:ring-2 focus:ring-blue-200"
                      />
                    </div>
                  </div>
                )}
              </div>
            )}

            {filter.type === 'event' && (
              <div ref={dropdownRef} className="relative">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Search Events
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Search className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    value={eventSearchTerm}
                    onChange={(e) => {
                      setEventSearchTerm(e.target.value);
                      setShowEventDropdown(true);
                    }}
                    onFocus={() => setShowEventDropdown(true)}
                    placeholder="Search by event name or date..."
                    className="block w-full pl-10 pr-3 py-2 bg-white/60 border border-gray-200 rounded-lg text-gray-800 placeholder-gray-500 focus:border-blue-300 focus:ring-2 focus:ring-blue-200"
                  />
                </div>
                {showEventDropdown && (
                  <LiquidGlass className="absolute z-10 mt-1 w-full max-h-60 overflow-auto">
                    {filteredEvents.length > 0 ? (
                      filteredEvents.map((event) => (
                        <button
                          key={event.id}
                          onClick={() => handleEventSelect(event)}
                          className="w-full px-4 py-2 text-left hover:bg-white/20 flex justify-between items-center text-gray-800 transition-colors duration-200"
                        >
                          <span className="font-medium">{event.name}</span>
                          <span className="text-sm text-gray-600">{formatDate(event.date)}</span>
                        </button>
                      ))
                    ) : (
                      <div className="px-4 py-2 text-sm text-gray-600">
                        No events found
                      </div>
                    )}
                  </LiquidGlass>
                )}
                {selectedEvent && (
                  <p className="mt-2 text-sm text-gray-600">
                    Selected: {selectedEvent.name} ({formatDate(selectedEvent.date)})
                  </p>
                )}
              </div>
            )}
          </div>
        </LiquidGlass>
      )}

      {error && (
        <LiquidGlass className="bg-red-100/60 border-red-200/60 p-4">
          <p className="text-red-700">{error}</p>
        </LiquidGlass>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <LiquidGlass variant="card" className="p-6">
          <div className="flex items-center gap-4">
            <div className="bg-blue-100 p-3 rounded-xl">
              <Calendar className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Total Events</p>
              <p className="text-3xl font-bold text-gray-800">{stats.totalEvents}</p>
            </div>
          </div>
        </LiquidGlass>

        <LiquidGlass variant="card" className="p-6">
          <div className="flex items-center gap-4">
            <div className="bg-emerald-100 p-3 rounded-xl">
              <Users className="w-6 h-6 text-emerald-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Total Participants</p>
              <p className="text-3xl font-bold text-gray-800">{stats.totalParticipants}</p>
            </div>
          </div>
        </LiquidGlass>

        <LiquidGlass variant="card" className="p-6">
          <div className="flex items-center gap-4">
            <div className="bg-purple-100 p-3 rounded-xl">
              <UserCheck className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Finishers</p>
              <p className="text-3xl font-bold text-gray-800">{stats.finishers}</p>
            </div>
          </div>
        </LiquidGlass>

        <LiquidGlass variant="card" className="p-6">
          <div className="flex items-center gap-4">
            <div className="bg-red-100 p-3 rounded-xl">
              <UserX className="w-6 h-6 text-red-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">DNF Rate</p>
              <p className="text-3xl font-bold text-gray-800">{stats.dnfRate.toFixed(2)}%</p>
            </div>
          </div>
        </LiquidGlass>
      </div>

      <LiquidGlass variant="card" className="p-6">
        <h2 className="text-lg font-semibold mb-4 text-gray-800">Gender Distribution</h2>
        <div className="space-y-4">
          {Object.entries(stats.genderDistribution).map(([gender, count]) => (
            <div key={gender}>
              <div className="flex justify-between text-sm text-gray-600 mb-1">
                <span>{gender}</span>
                <span>{count}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-black h-2 rounded-full transition-all duration-500"
                  style={{
                    width: `${stats.totalParticipants > 0 ? (count / stats.totalParticipants) * 100 : 0}%`
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      </LiquidGlass>

      {filter.type === 'event' && timeDistribution.length > 0 && (
        <LiquidGlass variant="card" className="p-6">
          <h2 className="text-lg font-semibold mb-4 text-gray-800">Finish Time Distribution</h2>
          <p className="text-sm text-gray-600 mb-4">
            Showing {timeDistribution.length} time brackets with outlier detection for optimal visualization
          </p>
          {loadingChart ? (
            <div className="h-[400px] flex items-center justify-center">
              <div className="relative">
                <div className="animate-spin rounded-full h-12 w-12 border-2 border-gray-200 border-t-blue-500"></div>
                <div className="absolute inset-0 rounded-full bg-gradient-to-r from-blue-100/20 to-purple-100/20"></div>
              </div>
            </div>
          ) : (
            <div className="h-[400px]">
              <Bar data={chartData} options={chartOptions} />
            </div>
          )}
        </LiquidGlass>
      )}
    </div>
  );
}